import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { extTokensApi } from '../lib/api';
import { EXT_VERSION } from '../lib/extension';

// Token estensione come restituito da GET /ext/tokens: include token_plain
// (copiabile anche dopo la creazione; null per i token vecchi, non recuperabili)
export interface ExtToken {
  id: string;
  label: string;
  last_used_at: string | null;
  created_at: string;
  revoked_at: string | null;
  token_plain?: string | null;
  ext_version?: string | null;
}

// Risposta di POST /ext/tokens: il token in chiaro esiste SOLO in questa risposta
export interface CreatedExtToken {
  id: string;
  label: string;
  token: string;
}

export function useExtTokens() {
  return useQuery({
    queryKey: ['ext-tokens'],
    queryFn: async () => {
      const res = await extTokensApi.list();
      return (res?.items ?? res?.tokens ?? res ?? []) as ExtToken[];
    },
  });
}

export function useCreateExtToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (label: string) => (await extTokensApi.create(label)) as CreatedExtToken,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ext-tokens'] }),
  });
}

export function useRevokeExtToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => extTokensApi.revoke(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ext-tokens'] }),
  });
}


// Confronto semver "a.b.c": true se `installed` e' piu' vecchia di `latest`
// (null = dispositivo su build pre-versioning, quindi da aggiornare).
export function isExtOutdated(installed: string | null, latest: string): boolean {
  if (!installed) return true;
  const a = installed.split('.').map((x) => parseInt(x, 10) || 0);
  const b = latest.split('.').map((x) => parseInt(x, 10) || 0);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const x = a[i] || 0;
    const y = b[i] || 0;
    if (x !== y) return x < y;
  }
  return false;
}

// True se almeno un dispositivo collegato (non revocato) usa una versione
// piu' vecchia di quella pubblicata: alimenta banner + puntino di alert nel menu.
export function useExtUpdateAvailable(): boolean {
  const { data: tokens } = useExtTokens();
  const active = (tokens ?? []).filter((t) => !t.revoked_at);
  if (active.length === 0) return false;
  return active.some((t) => isExtOutdated(t.ext_version ?? null, EXT_VERSION));
}
